// Per-category field schema for listings — mirror of the backend module at
// backend/src/domains/listing/categoryMetadata.js. Must stay in sync.
//
// requiredItemFields  — must be populated before publish (wizard blocks on them).
// optionalItemFields  — accepted and stored, but not enforced.
// sensitiveItemFields — backend redacts these from partner views until the
//                       listing is paid AND assigned to the viewing partner.
//
// For `boats`, `vin` stores the HIN and `licensePlate` stores the registration
// number. The wizard switches labels based on category.

export const CATEGORY_METADATA = {
  cars: {
    id: 'cars',
    label: 'Cars',
    requiredItemFields: ['make', 'model', 'year', 'vin', 'vehicleStatus', 'weight'],
    optionalItemFields: ['bodyType', 'generation', 'licensePlate'],
    sensitiveItemFields: ['vin', 'licensePlate'],
  },
  motorcycles: {
    id: 'motorcycles',
    label: 'Motorcycles & Scooters',
    requiredItemFields: ['make', 'model', 'year', 'vin', 'vehicleStatus', 'weight'],
    optionalItemFields: ['generation', 'licensePlate'],
    sensitiveItemFields: ['vin', 'licensePlate'],
  },
  other_vehicles: {
    id: 'other_vehicles',
    label: 'Other Vehicles',
    requiredItemFields: ['make', 'model', 'year', 'vin', 'vehicleStatus', 'weight', 'dimensions'],
    optionalItemFields: ['description', 'licensePlate'],
    sensitiveItemFields: ['vin', 'licensePlate'],
  },
  boats: {
    id: 'boats',
    label: 'Boats',
    requiredItemFields: ['make', 'model', 'year', 'vin', 'lengthMeters', 'weight'],
    optionalItemFields: ['description', 'licensePlate'],
    sensitiveItemFields: ['vin', 'licensePlate'],
  },
  furniture: {
    id: 'furniture',
    label: 'Furniture',
    requiredItemFields: ['itemDescription', 'quantity', 'dimensions', 'weight'],
    optionalItemFields: ['specialHandling', 'totalVolumeCbm'],
    sensitiveItemFields: [],
  },
  removals: {
    id: 'removals',
    label: 'Removals',
    requiredItemFields: [
      'totalVolumeCbm',
      'originFloor',
      'destFloor',
      'hasElevatorOrigin',
      'hasElevatorDest',
    ],
    optionalItemFields: ['itemDescription', 'weight', 'specialHandling'],
    sensitiveItemFields: [],
  },
  pallets: {
    id: 'pallets',
    label: 'Pallets',
    requiredItemFields: ['palletType', 'quantity', 'weight', 'dimensions'],
    optionalItemFields: ['specialHandling', 'totalVolumeCbm'],
    sensitiveItemFields: [],
  },
  freight: {
    id: 'freight',
    label: 'Freight',
    requiredItemFields: ['freightType', 'weight', 'dimensions'],
    optionalItemFields: ['quantity', 'totalVolumeCbm', 'specialHandling'],
    sensitiveItemFields: [],
  },
  parcels: {
    id: 'parcels',
    label: 'Parcels',
    requiredItemFields: ['quantity', 'weight', 'dimensions'],
    optionalItemFields: ['itemDescription'],
    sensitiveItemFields: [],
  },
  machinery: {
    id: 'machinery',
    label: 'Machinery & Equipment',
    requiredItemFields: ['itemDescription', 'weight', 'dimensions'],
    optionalItemFields: ['make', 'model', 'serialNumber', 'specialHandling'],
    sensitiveItemFields: ['serialNumber'],
  },
  pets: {
    id: 'pets',
    label: 'Pets',
    requiredItemFields: ['petType', 'breed', 'quantity'],
    optionalItemFields: ['specialHandling', 'itemDescription'],
    sensitiveItemFields: [],
  },
  building_materials: {
    id: 'building_materials',
    label: 'Building Materials',
    requiredItemFields: ['materialType', 'quantity', 'weight'],
    optionalItemFields: ['dimensions', 'specialHandling', 'totalVolumeCbm'],
    sensitiveItemFields: [],
  },
  fragile: {
    id: 'fragile',
    label: 'Fragile Goods',
    requiredItemFields: ['itemDescription', 'quantity', 'dimensions', 'specialHandling'],
    optionalItemFields: ['weight', 'totalVolumeCbm'],
    sensitiveItemFields: [],
  },
  bicycles: {
    id: 'bicycles',
    label: 'Bicycles',
    requiredItemFields: ['make', 'model', 'quantity'],
    optionalItemFields: ['year', 'weight', 'itemDescription'],
    sensitiveItemFields: [],
  },
  vehicle_parts: {
    id: 'vehicle_parts',
    label: 'Vehicle Parts',
    requiredItemFields: ['itemDescription', 'weight', 'dimensions'],
    optionalItemFields: ['make', 'model', 'serialNumber', 'quantity'],
    sensitiveItemFields: ['serialNumber'],
  },
  household: {
    id: 'household',
    label: 'Household Appliances',
    requiredItemFields: ['itemDescription', 'quantity', 'weight'],
    optionalItemFields: ['dimensions', 'serialNumber', 'totalVolumeCbm', 'specialHandling'],
    sensitiveItemFields: ['serialNumber'],
  },
  other: {
    id: 'other',
    label: 'Other',
    requiredItemFields: ['itemDescription', 'weight'],
    optionalItemFields: ['dimensions', 'quantity', 'specialHandling'],
    sensitiveItemFields: [],
  },
};

export const CATEGORY_IDS = Object.keys(CATEGORY_METADATA);

export function getCategoryMeta(categoryId) {
  const meta = CATEGORY_METADATA[categoryId];
  if (!meta) {
    throw new Error(`Unknown listing category: ${categoryId}`);
  }
  return meta;
}

// Convenience: is `field` required for `category`?
export function isRequiredField(category, field) {
  const meta = CATEGORY_METADATA[category];
  return !!meta && meta.requiredItemFields.includes(field);
}
